import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { translations } from "@/i18n/translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

const AppContext = createContext(null);

const LANG_KEY = "smartlife.lang";
const BUILDER_KEY = "smartlife.builder";

export function AppProvider({ children }) {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "tr";
    return localStorage.getItem(LANG_KEY) || "tr";
  });
  const [scenarios, setScenarios] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedScenarios, setSelectedScenarios] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      return JSON.parse(localStorage.getItem(BUILDER_KEY) || "[]");
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(LANG_KEY, lang);
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    localStorage.setItem(BUILDER_KEY, JSON.stringify(selectedScenarios));
  }, [selectedScenarios]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [s, p] = await Promise.all([
          axios.get(`${API}/scenarios`),
          axios.get(`${API}/products`),
        ]);
        if (!alive) return;
        setScenarios(s.data || []);
        setProducts(p.data || []);
      } catch (e) {
        console.error("Failed to load data", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const t = useMemo(() => translations[lang] || translations.tr, [lang]);

  const toggleScenario = useCallback((slug) => {
    setSelectedScenarios((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
  }, []);

  const clearBuilder = useCallback(() => setSelectedScenarios([]), []);

  // Compute aggregated product slugs from selected scenarios
  const requiredProductSlugs = useMemo(() => {
    const set = new Set();
    selectedScenarios.forEach((slug) => {
      const sc = scenarios.find((x) => x.slug === slug);
      if (sc) sc.products.forEach((p) => set.add(p));
    });
    return Array.from(set);
  }, [selectedScenarios, scenarios]);

  const requiredProducts = useMemo(() => {
    return requiredProductSlugs
      .map((slug) => products.find((p) => p.slug === slug))
      .filter(Boolean);
  }, [requiredProductSlugs, products]);

  const localized = useCallback(
    (obj, key) => (obj ? obj[`${key}_${lang}`] : ""),
    [lang]
  );

  const value = {
    lang,
    setLang,
    t,
    scenarios,
    products,
    loading,
    selectedScenarios,
    toggleScenario,
    clearBuilder,
    requiredProductSlugs,
    requiredProducts,
    localized,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
